import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface KmsDashboard {
  readonly articles: { readonly total: number; readonly published: number; readonly inReview: number; readonly draft: number };
  readonly sops: { readonly total: number; readonly published: number; readonly inReview: number };
  readonly policies: { readonly total: number; readonly published: number };
  readonly faqs: { readonly total: number; readonly published: number };
  readonly courses: { readonly total: number; readonly compliance: number };
  readonly training: { readonly assignments: number; readonly completed: number; readonly overdue: number };
  readonly certifications: { readonly total: number; readonly issued: number; readonly expiringSoon: number };
  readonly governance: { readonly reviewsDue: number };
}

const TOTALS: readonly { key: keyof KmsDashboard; sub: string; labelKey: string }[] = [
  { key: 'articles', sub: 'total', labelKey: 'm35.repo.articles' },
  { key: 'sops', sub: 'total', labelKey: 'm35.repo.sops' },
  { key: 'policies', sub: 'total', labelKey: 'm35.repo.policies' },
  { key: 'faqs', sub: 'total', labelKey: 'm35.repo.faqs' },
  { key: 'courses', sub: 'total', labelKey: 'm35.repo.courses' },
  { key: 'certifications', sub: 'total', labelKey: 'm35.repo.certifications' },
];

/**
 * M35 — Knowledge Repository Dashboard (§33). Read-only executive KPI roll-up
 * for the Enterprise Knowledge Management System: article / SOP / policy / FAQ
 * corpus totals with their lifecycle split (published / in-review / draft),
 * course catalogue and compliance-course counts, training throughput
 * (assignments / completed / overdue), certification health (issued /
 * expiring-soon) and the count of governed documents due for periodic review
 * (BR-06). Tenant isolation is enforced server-side (LAW 4).
 */
export function KnowledgeRepositoryDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState<KmsDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: KmsDashboard }>(`/v1/kms/dashboard`, { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="kms-dashboard">
      <h1>{t('m35.repo.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? <p>{t('m35.common.loading')}</p> : (
        <>
          <section aria-label="kms-totals" className="kpi-grid">
            {TOTALS.map(({ key, sub, labelKey }) => (
              <div key={key} className="kpi-card" data-kpi={key}>
                <span className="kpi-label">{t(labelKey)}</span>
                <span className="kpi-value" aria-label={`total-${key}`}>{(data[key] as Record<string, number>)[sub]}</span>
              </div>
            ))}
          </section>

          <dl aria-label="kms-health">
            <dt>{t('m35.repo.articlesPublished')}</dt>
            <dd aria-label="articles-published">{data.articles.published}</dd>
            <dt>{t('m35.repo.articlesInReview')}</dt>
            <dd aria-label="articles-inreview">{data.articles.inReview}</dd>
            <dt>{t('m35.repo.articlesDraft')}</dt>
            <dd aria-label="articles-draft">{data.articles.draft}</dd>
            <dt>{t('m35.repo.sopsPublished')}</dt>
            <dd aria-label="sops-published">{data.sops.published}</dd>
            <dt>{t('m35.repo.sopsInReview')}</dt>
            <dd aria-label="sops-inreview">{data.sops.inReview}</dd>
            <dt>{t('m35.repo.policiesPublished')}</dt>
            <dd aria-label="policies-published">{data.policies.published}</dd>
            <dt>{t('m35.repo.faqsPublished')}</dt>
            <dd aria-label="faqs-published">{data.faqs.published}</dd>
            <dt>{t('m35.repo.complianceCourses')}</dt>
            <dd aria-label="courses-compliance">{data.courses.compliance}</dd>
            <dt>{t('m35.repo.trainingAssignments')}</dt>
            <dd aria-label="training-assignments">{data.training.assignments}</dd>
            <dt>{t('m35.repo.trainingCompleted')}</dt>
            <dd aria-label="training-completed">{data.training.completed}</dd>
            <dt>{t('m35.repo.trainingOverdue')}</dt>
            <dd aria-label="training-overdue">{data.training.overdue}</dd>
            <dt>{t('m35.repo.certificationsIssued')}</dt>
            <dd aria-label="certifications-issued">{data.certifications.issued}</dd>
            <dt>{t('m35.repo.certificationsExpiring')}</dt>
            <dd aria-label="certifications-expiring">{data.certifications.expiringSoon}</dd>
            <dt>{t('m35.repo.reviewsDue')}</dt>
            <dd aria-label="governance-reviewsdue">{data.governance.reviewsDue}</dd>
          </dl>
        </>
      )}
    </main>
  );
}
